import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { InnerTextBlock, RichText } from "../../models";
import { ContentAlign } from "../../enums";
import {
  BlockHeader,
  HeaderLevel,
} from "../../../common/block-header/BlockHeader";
import { BlockBody } from "../../../common/block-body/BlockBody";
import { ContentViewVariant } from "./enums";

type Props = {
  title: RichText;
  text: RichText;
  titleSide: ContentAlign;
  details: InnerTextBlock[];
  variant?: ContentViewVariant;
};

export const Benefits: FC<Props> = ({
  title,
  text,
  titleSide,
  details,
  variant,
}) => (
  <div
    className={classNames(
      "mt-20 grid grid-cols-1 gap-20 px-5",
      "lg:mt-25 lg:gap-15 lg:px-16",
      "xl:mt-40 xl:gap-10 xl:px-24",
      "xl:mx-auto xl:max-w-container-wide",
      { "lg:grid-cols-3": variant === ContentViewVariant.THREE_COLUMNS },
      { "lg:grid-cols-2": variant !== ContentViewVariant.THREE_COLUMNS },
    )}
  >
    <div
      className={classNames({
        "lg:order-2 lg:flex lg:flex-col lg:items-end":
          titleSide === ContentAlign.RIGHT,
      })}
    >
      <div className="xl:max-w-[74%]">
        <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
          <div
            className={classNames(
              "text-medium text-primary-content",
              "lg:text-large",
              "xl:text-xx-large",
              { "mb-2.5 lg:mb-5": text },
            )}
          >
            <BlockHeader richText={title} />
          </div>
          <div className="text-regular text-base-400">
            <BlockBody richText={text} />
          </div>
        </Fade>
      </div>
    </div>
    <div
      className={classNames(
        "grid grid-cols-1 gap-y-5",
        {
          "lg:order-1": titleSide === ContentAlign.RIGHT,
        },
        {
          "md:grid-cols-2 md:gap-x-15 lg:col-span-2 xl:gap-x-10":
            variant === ContentViewVariant.THREE_COLUMNS,
        },
      )}
    >
      {details.map((block) => (
        <div
          key={block._uid}
          className={classNames({
            "xl:max-w-[74%]": variant !== ContentViewVariant.THREE_COLUMNS,
          })}
        >
          <Fade duration={DEFAULT_FADE_DURATION} triggerOnce>
            <div
              className={classNames(
                "text-medium text-primary-content",
                "lg:text-medium",
                {
                  "mb-2.5 lg:mb-5": text,
                },
              )}
            >
              <BlockHeader
                richText={block.title}
                headerLevel={HeaderLevel.H4}
              />
            </div>
            <div className="text-regular text-base-400">
              <BlockBody richText={block.text} />
            </div>
          </Fade>
        </div>
      ))}
    </div>
  </div>
);
